/**
 * PR-Cf — OWNER-only impersonation banner + dropdown.
 *
 * Two render modes:
 *   - mode="banner"   → sticky top-of-viewport amber banner shown
 *                       only when impersonation is ACTIVE.
 *   - mode="dropdown" → sidebar-footer button that toggles a user
 *                       picker. Hidden when impersonation is active
 *                       (we don't want two interaction points fighting
 *                       — the banner's "Stop" button is the only exit).
 *
 * Behaviour:
 *   - "Impersonate user" dropdown lists every active user except the
 *     OWNER themselves. Picking a row writes
 *     ``localStorage.reformity_impersonate_uid`` and forces a full
 *     page reload (cheapest way to flush every cached fetch + every
 *     place that read user.role from the AuthContext).
 *   - When active, the banner shows the impersonated user's name +
 *     role and exposes a "Stop impersonating" button.
 *
 * The backend (api/rbac.py::get_scope) is the source of truth on
 * whether an impersonation is allowed. The dropdown here is just a
 * convenience UI for OWNERs to debug "what does this rep see?".
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, getImpersonateUserId, setImpersonateUserId } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ShieldCheck, X, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";


function useImpersonationState() {
  const { user } = useAuth();
  const [activeUid, setActiveUid] = useState(() => getImpersonateUserId());
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const isOwner = user?.role === "OWNER";

  useEffect(() => {
    if (!isOwner) return undefined;
    let cancelled = false;
    const loadUsers = async () => {
      // Schedule the loading flag flip in a microtask so the
      // react-hooks/set-state-in-effect rule (which forbids
      // synchronous setState calls inside an effect body) doesn't
      // fire. Functionally equivalent to ``setLoading(true)`` on
      // line 1 of the effect.
      setLoading(true);
      try {
        const res = await api.get("/users?limit=200");
        if (cancelled) return;
        const filtered = (res.data || []).filter(
          (u) =>
            u.id !== user?.id &&
            (u.status || (u.is_active ? "active" : "deactivated")) === "active",
        );
        setUsers(filtered);
      } catch (_err) {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [isOwner, user?.id]);

  return { isOwner, activeUid, setActiveUid, users, loading };
}


function startImpersonate(targetId, setActiveUid) {
  setImpersonateUserId(targetId);
  setActiveUid(targetId);
  toast.success("Impersonation started — reloading…");
  setTimeout(() => window.location.reload(), 400);
}


function stopImpersonate(setActiveUid) {
  setImpersonateUserId(null);
  setActiveUid(null);
  toast.message("Impersonation stopped — reloading…");
  setTimeout(() => window.location.reload(), 400);
}


function ImpersonationActiveBanner() {
  const { isOwner, activeUid, setActiveUid, users } = useImpersonationState();
  const activeUser = useMemo(
    () => users.find((u) => u.id === activeUid),
    [users, activeUid],
  );
  if (!isOwner || !activeUid) return null;
  return (
    <div
      className="sticky top-0 z-40 bg-amber-500 text-black px-3 py-1.5 text-xs sm:text-sm flex items-center gap-2 shadow"
      data-testid="impersonation-banner"
    >
      <ShieldCheck size={14} weight="bold" />
      <span className="font-medium">
        Impersonating{" "}
        <strong>{activeUser?.name || activeUid.slice(0, 8)}</strong>
        {activeUser?.role && (
          <span className="ml-1 opacity-70">({activeUser.role})</span>
        )}
      </span>
      <button
        type="button"
        onClick={() => stopImpersonate(setActiveUid)}
        className="ml-auto inline-flex items-center gap-1 bg-black/15 hover:bg-black/25 rounded-sm px-2 py-0.5"
        data-testid="impersonation-stop-btn"
      >
        <X size={12} weight="bold" /> Stop
      </button>
    </div>
  );
}


function ImpersonationDropdown() {
  const { isOwner, activeUid, setActiveUid, users, loading } =
    useImpersonationState();
  const [open, setOpen] = useState(false);

  if (!isOwner) return null;
  // Hide the dropdown when impersonation is already active — the
  // banner's "Stop" button is the only exit point.
  if (activeUid) return null;

  return (
    <div className="relative" data-testid="impersonation-dropdown-root">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full inline-flex items-center justify-between gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors py-1.5 px-2 rounded-sm border border-dashed border-border"
        data-testid="impersonation-trigger-btn"
      >
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck size={12} weight="bold" /> Impersonate
        </span>
        <CaretDown
          size={12}
          weight="bold"
          className={
            open ? "rotate-180 transition-transform" : "transition-transform"
          }
        />
      </button>
      {open && (
        <div
          className="absolute left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-card border border-border rounded-sm shadow-xl z-50"
          data-testid="impersonation-dropdown-list"
        >
          {loading && (
            <div className="p-3 text-xs text-muted-foreground">
              Loading users…
            </div>
          )}
          {!loading && users.length === 0 && (
            <div className="p-3 text-xs text-muted-foreground">
              No other active users.
            </div>
          )}
          {users.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => startImpersonate(u.id, setActiveUid)}
              className="w-full text-left px-3 py-2 text-xs hover:bg-secondary border-b border-border last:border-b-0"
              data-testid={`impersonation-row-${u.id}`}
            >
              <div className="font-semibold truncate">{u.name}</div>
              <div className="text-muted-foreground truncate">
                {u.email} · {u.role}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


export default function ImpersonationBanner({ mode = "banner" }) {
  if (mode === "dropdown") {
    return <ImpersonationDropdown />;
  }
  return <ImpersonationActiveBanner />;
}

export { ImpersonationActiveBanner, ImpersonationDropdown };
